/**
 * Director Board Resolution Signing Component
 * Handles document viewing and signing with optional liveness verification
 */

import { FC, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Center, Loader, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ToastContainer, toast } from "react-toastify";
import DocViewer from "@cyntler/react-doc-viewer";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { Button } from "@funded-here-interface/common";
import { setRegtankLiveness } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import {
  LivenessWebVerifyStatusEnum,
  Persona,
} from "@funded-here-interface/common/src/constant/enum";
import { isValidRegtankUrl } from "@funded-here-interface/common/src/Utils/urlValidation";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUser";
import { useDocumentVisibility } from "@funded-here-interface/common/src/hooks/useDocumentVisibility";

import { RootState } from "../../store";
import { REST_SERVER, WEB_DOMAIN } from "../../constants/domain";
import { useBoardResolutionSign } from "../../hooks/useDocuSign";
import useSignedDocument from "../../hooks/useSignedDocument";
import useGetLivenessWebVerifyLink from "../../hooks/useGetLivenessWebVerifyLink";
import useGetRegtankLivenessWebVerifyStatus from "../../hooks/useGetRegtankLivenessWebVerifyStatus";
import { useRegtankLivenessSession } from "../../hooks/useRegtankLivenessSession";
import { useRegtankLivenessValidation } from "../../hooks/useRegtankLivenessValidation";
import useCompleteOnboarding from "../../hooks/useCompleteOnboarding";

import { useStyles } from "./ShareholderBoardResolution.styles";
import {
  useDocumentState,
  useButtonState,
  FailedLivenessModal,
  COLORS,
  MESSAGES,
} from "../../components/DocumentSigning";
import { navigateToThanksPage } from "../ThanksPage/ThanksPageUtils";
import { ThanksPageType } from "../../constants/enums";

const AGREEMENT_NAME = "Board Resolution" as const;

// Custom acknowledgement message for Shareholder/Director Board Resolution
const DIRECTOR_ACKNOWLEDGEMENT =
  'By clicking "Proceed to Sign", you acknowledge that you have read and understood the above Board Resolution and are signing in your capacity as a director.' as const;

const DirectorBoardResolution: FC = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const {
    token,
    regtankRequest,
    regtankLiveness,
    regtankLivenessFeatureFlag,
    regtankOnboardingFeatureFlag,
    regtankOnboarding,
    role,
  } = useSelector((state: RootState) => state.auth);

  // API hooks
  const boardResolutionMutation = useBoardResolutionSign();
  const signedDocumentMutation = useSignedDocument();
  const livenessLinkMutation = useGetLivenessWebVerifyLink();
  const livenessStatusMutation = useGetRegtankLivenessWebVerifyStatus();
  const completeOnboardingMutation = useCompleteOnboarding();
  const { data, refetch } = useGetUser(token);

  // Local state
  const [regtankLivenessUrl, setRegtankLivenessUrl] = useState("");
  const [openedFailedLiveness, modalAction] = useDisclosure();

  // Custom hooks
  const documentState = useDocumentState();
  const livenessSession = useRegtankLivenessSession(
    documentState.documentId,
    data?.user?.regtankLiveness || regtankLiveness
  );
  const livenessValidation = useRegtankLivenessValidation(livenessSession);
  const buttonState = useButtonState({
    regtankLivenessFeatureFlag: regtankLivenessFeatureFlag ?? null,
    regtankOnboardingFeatureFlag: regtankOnboardingFeatureFlag ?? null,
    regtankOnboarding: regtankOnboarding ?? null,
    regtankRequest,
    isDisabledByLiveness: livenessValidation.isDisabled,
  });

  // Document viewer config
  const docs = [{ uri: documentState.realUrl, fileType: "pdf" as const }];
  const redirectUrl = window.location.href;

  /**
   * Generate Board Resolution document
   */
  const handleGenerateDocument = async () => {
    toast.info(MESSAGES.LOADING_DOC, { toastId: "loading-doc-board-reso" });

    try {
      boardResolutionMutation.mutate(
        {
          token,
          webDomain: WEB_DOMAIN,
          redirectUrl: `${REST_SERVER}/document-management/docusign/webhook`,
          directCreateDocusign: false,
        },
        {
          onSuccess: async (data) => {
            await refetch();
            documentState.updateDocument(
              data.url,
              data.documentId,
              data.completedAt || ""
            );
            documentState.incrementViewerKey();
          },
          onError: (e) => {
            toast.error((e as Error).message);
          },
        }
      );
    } catch (ex) {
      toast.error("Error generating Board Resolution");
    }
  };

  /**
   * Navigate to next screen based on user status
   */
  const moveNext = async () => {
    const { data: freshData } = await refetch();
    if (
      freshData.user.allShareHoldersDirectorsOnBoarded &&
      freshData.user.isAuthRep
    ) {
      // Navigate based on role
      if (role === Persona.INVESTOR) {
        navigate("/declaration-ai");
      } else if (role === Persona.MERCHANT) {
        navigate("/merchant/onboarding/membership-agreement");
      } else if (role === Persona.ISPV) {
        navigate("/issuer-agreement");
      } else {
        // Default fallback for other roles
        navigate("/declaration-ai");
      }
    } else {
      navigateToThanksPage(navigate, ThanksPageType.BOARDING_RESOLUTION);
    }
  };

  /**
   * Sign the document and complete onboarding
   */
  const handleSign = () => {
    documentState.clearUrl();

    signedDocumentMutation.mutate(
      { documentId: documentState.documentId, token },
      {
        onSuccess: (data) => {
          // Trigger complete onboarding
          completeOnboardingMutation.mutate(
            { token },
            {
              onSuccess: () => {
                sessionStorage.removeItem("regtankSessionId");
                toast.success(MESSAGES.SIGNING_SUCCESS);
                documentState.updateDocument(
                  data.url,
                  data.documentId,
                  data.completedAt
                );
                documentState.incrementViewerKey();
              },
              onError: (e) => {
                toast.error((e as Error).message);
              },
            }
          );
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  /**
   * Handle liveness verification or direct signing
   */
  const handleLivenessVerify = async () => {
    buttonState.disableButton("");

    if (!regtankLivenessFeatureFlag) {
      handleSign();
      return;
    }
    await refetch();
    // Use existing liveness URL if available
    if (regtankLivenessUrl) {
      if (!isValidRegtankUrl(regtankLivenessUrl)) {
        toast.error(MESSAGES.INVALID_URL);
        return;
      }
      window.open(regtankLivenessUrl, "_self");
      return;
    }

    // Generate new liveness URL
    const agreementName =
      role === Persona.INVESTOR
        ? "Board reso (SG Investors)"
        : "Board reso (SG Merchants)";

    toast.info(MESSAGES.LOADING_LIVENESS, {
      toastId: "loading-regtank-liveness-check-url",
    });

    livenessLinkMutation.mutate(
      {
        token,
        documentId: documentState.documentId,
        redirectUrl,
        agreementName,
      },
      {
        onSuccess: (data) => {
          if (!isValidRegtankUrl(data.verifyLink)) {
            toast.error(MESSAGES.INVALID_URL);
            return;
          }

          sessionStorage.setItem("regtankSessionId", data.sessionId);

          dispatch(
            setRegtankLiveness({
              regtankLiveness: [
                {
                  id: documentState.documentId.toString(),
                  clientSessionId: documentState.documentId.toString(),
                  sessionId: data.sessionId,
                  livenessStatus: null,
                  regtankLivenessUrl: data.verifyLink,
                  noOfAttemptLiveness: 0,
                  reattemptApproval: null,
                  expiredDate: data.expiredDate,
                },
              ],
            })
          );

          window.open(data.verifyLink, "_self");
          documentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
          documentState.incrementViewerKey();
        },
      }
    );
  };

  /**
   * Handle modal continue action
   */
  const handleContinue = () => {
    buttonState.disableButton(MESSAGES.VERIFICATION_DISABLED);
    modalAction.close();
    sessionStorage.removeItem("regtankSessionId");
    documentState.incrementViewerKey();
  };

  // Initial load: generate document
  useEffect(() => {
    handleGenerateDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate document when page becomes visible again after switching tabs or inactive for a while
  useDocumentVisibility(
    () => documentState.incrementViewerKey(),
    !!documentState.documentId
  );

  // Verify liveness status when needed
  useEffect(() => {
    if (!livenessSession || !livenessValidation.shouldVerify) return;

    livenessStatusMutation.mutate(
      { token, sessionId: livenessSession.sessionId },
      {
        onSuccess: (data: any) => {
          if (data === LivenessWebVerifyStatusEnum.SUCCESS) {
            handleSign();
          } else if (
            data === LivenessWebVerifyStatusEnum.LIVENESS_FAILED ||
            data === LivenessWebVerifyStatusEnum.FAILED
          ) {
            modalAction.open();
          }
          documentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livenessSession, livenessValidation.shouldVerify, token]);

  // Set liveness URL when user can attempt verification
  useEffect(() => {
    if (livenessValidation.canAttempt) {
      setRegtankLivenessUrl(livenessSession?.regtankLivenessUrl || "");
    }
  }, [livenessValidation.canAttempt, livenessSession]);

  return (
    <>
      <ToastContainer />
      <div>
        <Text className={classes.heading}>{AGREEMENT_NAME}</Text>

        {documentState.isDocumentLoaded && (
          <>
            <DocViewer
              documents={docs}
              style={{ width: "100%", height: 500, overflow: "auto" }}
              key={documentState.viewerKey}
              className={classes.docviewer}
              prefetchMethod="GET"
              config={{
                header: {
                  disableHeader: false,
                  disableFileName: true,
                  retainURLParams: false,
                },
                pdfVerticalScrollByDefault: true,
              }}
            />

            {!documentState.isDocumentSigned && (
              <div className={classes.buttonContainer}>
                <Text style={{ marginBottom: 10, fontSize: 14 }}>
                  {DIRECTOR_ACKNOWLEDGEMENT}
                </Text>
                <Button
                  disabled={buttonState.buttonDisabled}
                  onClick={handleLivenessVerify}
                  textColor="white"
                  backgroundColor={COLORS.PRIMARY}
                  title={buttonState.titleButton}
                >
                  Proceed to Sign
                </Button>
              </div>
            )}

            {documentState.isDocumentSigned && (
              <div className={classes.buttonContainer}>
                <Button
                  onClick={moveNext}
                  textColor="white"
                  backgroundColor={COLORS.PRIMARY}
                >
                  Continue
                </Button>
              </div>
            )}

            <FailedLivenessModal
              opened={openedFailedLiveness}
              onClose={modalAction.close}
              onContinue={handleContinue}
              classes={classes}
            />
          </>
        )}

        {!documentState.isDocumentLoaded && (
          <Center maw={400} h={500} mx="auto">
            <Loader color="teal" />
          </Center>
        )}
      </div>
    </>
  );
};

export default DirectorBoardResolution;
